import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Calculator as CalcIcon, Lightbulb, ListChecks, Gauge, Loader2 } from "lucide-react";
import { Calculator } from "@/components/Calculator";
import { AdSlot } from "@/components/AdSlot";
import { Faq } from "@/components/Faq";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getDeviceBySlug, deviceList } from "@/data/devices";
import { getCustomDevice } from "@/lib/api";

export default function SeoLandingPage() {
  const { seoSlug } = useParams();
  const staticDevice = getDeviceBySlug(seoSlug);
  const [device, setDevice] = useState(staticDevice || null);
  const [loading, setLoading] = useState(!staticDevice);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (staticDevice) {
      setDevice(staticDevice);
      setLoading(false);
      return;
    }
    // Nicht in den statischen Daten: könnte ein KI-generiertes (veröffentlichtes) Gerät sein.
    setLoading(true);
    let active = true;
    getCustomDevice(seoSlug).then((d) => {
      if (active) {
        setDevice(d);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seoSlug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-24 text-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        <p className="mt-3 text-sm">Rechner wird geladen …</p>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center">
        <Seo title="Seite nicht gefunden | Gerätekosten" description="Diese Seite wurde nicht gefunden." />
        <h1 className="text-2xl font-semibold">Seite nicht gefunden</h1>
        <p className="text-muted-foreground mt-2">Diesen Rechner gibt es nicht.</p>
        <Link to="/" className="text-primary underline mt-4 inline-block">Zur Startseite</Link>
      </div>
    );
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = `${origin}/${device.slug}`;
  const related = deviceList.filter((d) => d.key !== device.key);
  const faq = device.faq || [];
  const drivers = device.drivers || [];

  const jsonLd = [
    ...(faq.length
      ? [
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : []),
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: origin || undefined },
        { "@type": "ListItem", position: 2, name: device.name, item: pageUrl },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: `${device.name} Stromkostenrechner`,
      applicationCategory: "UtilitiesApplication",
      operatingSystem: "Web",
      url: pageUrl,
      inLanguage: "de-DE",
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
      description: device.metaDescription,
    },
  ];

  return (
    <div>
      <Seo title={device.metaTitle} description={device.metaDescription} canonical={pageUrl} />
      <JsonLd data={jsonLd} />

      {/* Hero + Tool above the fold */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(15,118,110,0.08), rgba(249,115,22,0.06), rgba(251,250,247,0))",
            height: "50%",
          }}
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-6 relative">
          <div className="lg:grid lg:grid-cols-[1fr_320px] lg:gap-8">
            <div>
              <Badge variant="secondary" className="mb-3">{device.name}</Badge>
              {device.custom && (
                <span className="ml-2 text-xs text-muted-foreground align-middle">KI-Schätzung</span>
              )}
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{device.h1}</h1>
              {device.intro && <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed">{device.intro}</p>}
              <div className="mt-6">
                <Calculator device={device} />
              </div>
            </div>
            <div className="hidden lg:block"><AdSlot variant="rail" /></div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:hidden mt-4"><AdSlot variant="inline" /></div>

      {/* Erklärung + Beispielrechnung */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid gap-6 lg:grid-cols-2">
          {device.explanation && (
            <Card className="p-5">
              <div className="flex items-center gap-2 font-heading font-semibold">
                <CalcIcon className="h-5 w-5 text-primary" /> So wird gerechnet
              </div>
              <p className="mt-3 text-muted-foreground leading-relaxed">{device.explanation}</p>
            </Card>
          )}
          {device.example && (
            <Card className="p-5" data-testid="example-box">
              <div className="flex items-center gap-2 font-heading font-semibold">
                <Lightbulb className="h-5 w-5 text-primary" /> Beispielrechnung
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{device.example.label}</p>
              <ul className="mt-3 space-y-1.5 text-sm">
                {(device.example.lines || []).map((line, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        {drivers.length > 0 && (
          <Card className="p-5 mt-6">
            <div className="flex items-center gap-2 font-heading font-semibold">
              <ListChecks className="h-5 w-5 text-primary" /> Was die Stromkosten beeinflusst
            </div>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {drivers.map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6"><AdSlot variant="inline" /></div>

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <h2 className="text-2xl font-semibold tracking-tight mb-4">Häufige Fragen zu {device.name} und Stromkosten</h2>
          <Faq items={faq} />
        </section>
      )}

      <section className="max-w-5xl mx-auto px-4 sm:px-6">
        <Card className="p-5 bg-muted/30">
          <div className="flex items-start gap-3">
            <Gauge className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              Alle Werte sind Schätzungen auf Basis typischer Verbrauchsbereiche. Den genauen Verbrauch deines Geräts
              misst du am zuverlässigsten mit einem Strommessgerät, das du einfach zwischen Steckdose und Gerät steckst.
            </p>
          </div>
        </Card>
      </section>

      {/* Interne Links zu verwandten Geräten */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="text-xl font-semibold tracking-tight mb-4">Weitere Geräte berechnen</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {related.slice(0, 8).map((d) => (
            <Link
              key={d.key}
              to={`/${d.slug}`}
              data-testid={`related-link-${d.key}`}
              className="group flex items-center justify-between rounded-xl border p-4 hover:border-primary hover:bg-primary/5 transition-colors"
            >
              <span className="font-medium">{d.name} Stromkosten</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
            </Link>
          ))}
          <Link
            to="/stromkostenrechner"
            className="group flex items-center justify-between rounded-xl border p-4 hover:border-primary hover:bg-primary/5 transition-colors"
          >
            <span className="font-medium">Allgemeiner Stromkostenrechner</span>
            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
          </Link>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-10"><AdSlot variant="inline" /></div>
    </div>
  );
}
